import { Col, Row, Spin, Table, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getFullName,
  reportDate,
  setPaymentStatus,
  stringDate,
  stringDateTime,
} from '../../../../util';
import { formatPrice } from '../../../../util/formatPrice';
import {
  onlineSaleDetailConst,
  paymentProcessTypesConst,
  treatmentStatusDictionary,
} from '../../../../constants/Sales';
import { label } from '../../../../constants';
import { getOnlineSaleOrderDetail } from '../../../../appRedux/actions/Sales';

export const SalesModalBody = ({
  data = {},
  saleType,
  showOrderTracking = false,
}) => {
  const dispatch = useDispatch();
  const [onlineDetail, setOnlineDetail] = useState(null);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);
  const santanderPayment = data?.santanderPayment;

  useEffect(() => {
    let active = true;

    const loadOrderDetail = async () => {
      if (!showOrderTracking || !data?.id) return;

      setLoadingOrderDetail(true);
      const detail = await dispatch(getOnlineSaleOrderDetail(data.id));

      if (active) {
        setOnlineDetail(detail || null);
        setLoadingOrderDetail(false);
      }
    };

    loadOrderDetail();
    return () => {
      active = false;
    };
  }, [data?.id, dispatch, showOrderTracking]);

  const branchName = (branch) =>
    typeof branch === 'string'
      ? branch
      : branch?.name || onlineSaleDetailConst.EMPTY_VALUE;

  const columns = [
    {
      title: 'Articulo',
      dataIndex: 'package',
      key: 'package',
      render: (pkg, item) => (pkg?.name ? pkg?.name : item?.treatment?.name),
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Precio',
      dataIndex: 'salePrice',
      key: 'salePrice',
      render: (price, item) => formatPrice(price * item?.quantity),
    },
    {
      title: 'Total',
      dataIndex: 'price',
      key: 'price',
      render: (price, item) => formatPrice(price * item?.quantity),
    },
  ];

  const treatmentColumns = [
    {
      title: onlineSaleDetailConst.COLUMNS.TREATMENT,
      dataIndex: 'treatment',
      key: 'treatment',
    },
    {
      title: onlineSaleDetailConst.COLUMNS.STATUS,
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        treatmentStatusDictionary[status] ||
        status ||
        onlineSaleDetailConst.EMPTY_VALUE,
    },
    {
      title: onlineSaleDetailConst.COLUMNS.PURCHASED,
      dataIndex: 'purchasedSessions',
      key: 'purchasedSessions',
      align: 'center',
    },
    {
      title: onlineSaleDetailConst.COLUMNS.USED,
      dataIndex: 'usedSessions',
      key: 'usedSessions',
      align: 'center',
    },
    {
      title: onlineSaleDetailConst.COLUMNS.AVAILABLE,
      dataIndex: 'availableSessions',
      key: 'availableSessions',
      align: 'center',
    },
    {
      title: onlineSaleDetailConst.COLUMNS.EXPIRATION_DATE,
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date) => reportDate(date),
    },
  ];

  const sessionColumns = [
    {
      title: onlineSaleDetailConst.COLUMNS.TREATMENT,
      dataIndex: 'treatment',
      key: 'treatment',
    },
    {
      title: onlineSaleDetailConst.COLUMNS.DATE_TIME,
      dataIndex: 'attendedAt',
      key: 'attendedAt',
      render: (date) => stringDateTime(date),
    },
    {
      title: onlineSaleDetailConst.COLUMNS.BRANCH,
      dataIndex: 'branch',
      key: 'branch',
      render: branchName,
    },
    {
      title: onlineSaleDetailConst.COLUMNS.ATTENDED_BY,
      dataIndex: 'attendedBy',
      key: 'attendedBy',
      render: (user) => user?.name || onlineSaleDetailConst.EMPTY_VALUE,
    },
    {
      title: onlineSaleDetailConst.COLUMNS.REGISTERED_BY,
      dataIndex: 'registeredBy',
      key: 'registeredBy',
      render: (user) => user?.username || onlineSaleDetailConst.EMPTY_VALUE,
    },
  ];

  const transferColumns = [
    {
      title: onlineSaleDetailConst.COLUMNS.ORIGIN_BRANCH,
      dataIndex: 'originBranch',
      key: 'originBranch',
      render: branchName,
    },
    {
      title: onlineSaleDetailConst.COLUMNS.DESTINATION_BRANCH,
      dataIndex: 'destinationBranch',
      key: 'destinationBranch',
      render: branchName,
    },
    {
      title: onlineSaleDetailConst.COLUMNS.REASON,
      dataIndex: 'reason',
      key: 'reason',
    },
    {
      title: onlineSaleDetailConst.COLUMNS.DATE_TIME,
      dataIndex: 'transferredAt',
      key: 'transferredAt',
      render: (date) => stringDateTime(date),
    },
    {
      title: onlineSaleDetailConst.COLUMNS.EXECUTED_BY,
      dataIndex: 'executedBy',
      key: 'executedBy',
      render: (user) => user?.username || onlineSaleDetailConst.EMPTY_VALUE,
    },
  ];
  return (
    <>
      <Row className="row">
        <Col span={4}>
          <h4>No. Orden</h4>
        </Col>
        <Col span={8}>{data?.orderNumber || '-'}</Col>
        <Col span={4}>
          <h4 className="strong"> Vendedor</h4>
        </Col>
        <Col span={8}>{getFullName(data?.seller)}</Col>
      </Row>

      {showOrderTracking && (
        <Row className="row">
          <Col span={4}>
            <h4>{onlineSaleDetailConst.INITIAL_REDEMPTION_BRANCH}</h4>
          </Col>
          <Col span={8}>
            {branchName(onlineDetail?.initialRedemptionBranch)}
          </Col>
          <Col span={4}>
            <h4>{onlineSaleDetailConst.RESPONSIBLE_REDEMPTION_BRANCH}</h4>
          </Col>
          <Col span={8}>
            {branchName(onlineDetail?.responsibleRedemptionBranch)}
          </Col>
        </Row>
      )}

      <Row className="row">
        <Col span={4}>
          <h4>
            {showOrderTracking
              ? onlineSaleDetailConst.ACQUISITION_DATE
              : label.date}
          </h4>
        </Col>
        <Col span={8}>{stringDate(data?.created_at) || '-'}</Col>
        <Col span={4}>
          <h4>Sucursal</h4>
        </Col>
        <Col span={8}>{data?.branch?.name || '-'}</Col>
      </Row>

      <Row className="row">
        <Col span={4}>
          <h4 className="strong"> Cliente</h4>
        </Col>
        <Col span={8}>{getFullName(data?.customer)}</Col>
        <Col span={4}>
          <h4>Descuento</h4>
        </Col>
        <Col span={8}>
          {data?.discountTotal ? formatPrice(data?.discountTotal) : '-'}
          {data?.coupon && (
            <Tag style={{ marginLeft: 5 }}>{data.coupon?.name}</Tag>
          )}
        </Col>
      </Row>

      <Row className="row">
        <Col span={4}>
          <h4>Puntos</h4>
        </Col>
        <Col span={8}>
          <Tag>{data?.points}</Tag>
        </Col>
        <Col span={4}>
          <h4>Total</h4>
        </Col>
        <Col span={8}>{data?.total ? formatPrice(data?.total) : '-'}</Col>
      </Row>

      <Row className="row">
        <Col span={4}>
          <h4>Método de pago</h4>
        </Col>
        <Col span={8}>{data?.paymentMethod || '-'}</Col>
        <Col span={4}>
          <h4>Status</h4>
        </Col>
        <Col span={8}>
          <Tag color={data?.refundedAt ? 'red' : 'blue'}>
            {data?.refundedAt ? 'Devolución' : 'Venta'}
          </Tag>
        </Col>
      </Row>

      <Row className="row">
        <Col span={4}>
          <h4>Estado de pago</h4>
        </Col>
        <Col span={saleType === 'online' ? 8 : 20}>
          {saleType === 'online'
            ? setPaymentStatus(data?.paymentStatus)
            : data?.refundedAt
            ? 'Devolución'
            : 'Pagado'}
        </Col>
        {saleType === 'online' && (
          <>
            <Col span={4}>
              <h4>Procesador</h4>
            </Col>
            <Col span={8}>{data?.paymentGateway || '-'}</Col>
          </>
        )}
      </Row>

      <Row className="row">
        <Col span={4}>
          <h4>Referencia débito</h4>
        </Col>
        <Col span={8}>
          {data?.payReferenceDebit > 0 ? data?.payReferenceDebit : '-'}
        </Col>
        <Col span={4}>
          <h4>{label.branchFolio}</h4>
        </Col>
        <Col span={8}>{data?.folioTicket}</Col>
      </Row>

      <Row className="row">
        <Col span={4}>
          <h4>Referencia crédito</h4>
        </Col>
        <Col span={8}>
          {data?.payReferenceCredit > 0 ? data?.payReferenceCredit : '-'}
        </Col>

        {data?.refundMethod && (
          <>
            <Col span={4}>
              <h4>Método de devolución</h4>
            </Col>
            <Col span={8}>{data?.refundMethod || '-'}</Col>
          </>
        )}
      </Row>

      <Row className="row">
        <Col span={4}>
          <h4>Referencia de compra</h4>
        </Col>
        <Col span={8}>{data?.payReference || '-'}</Col>
        {data?.refundReason && (
          <>
            <Col span={4}>
              <h4>Razón de devolución</h4>
            </Col>
            <Col span={8}>{data?.refundReason || '-'}</Col>
          </>
        )}
      </Row>
      {data?.paymentGateway === paymentProcessTypesConst.santander && (
        <>
          <Row className="row">
            <Col span={4}>
              <h4>Reference ID</h4>
            </Col>
            <Col span={8}>{santanderPayment?.id || '-'}</Col>

            <Col span={4}>
              <h4>Folio Pagos</h4>
            </Col>
            <Col span={8}>{data?.payReference || '-'}</Col>
          </Row>

          <Row className="row">
            <Col span={4}>
              <h4>Amount</h4>
            </Col>
            <Col span={8}>
              {santanderPayment?.amount
                ? formatPrice(santanderPayment?.amount)
                : '-'}
            </Col>

            <Col span={4}>
              <h4>ccMASK</h4>
            </Col>
            <Col span={8}>{santanderPayment?.ccMask || '-'}</Col>
          </Row>

          <Row className="row">
            <Col span={4}>
              <h4>Auth</h4>
            </Col>
            <Col span={8}>{santanderPayment?.auth || '-'}</Col>

            <Col span={4}>
              <h4>IDURL</h4>
            </Col>
            <Col span={8}>{santanderPayment?.idUrl || '-'}</Col>
          </Row>
        </>
      )}

      <Row className="row">
        <Col span={24}>
          <Table
            pagination={{
              defaultPageSize: 3,
            }}
            dataSource={data.orderDetails}
            columns={columns}
          />
        </Col>
      </Row>

      {showOrderTracking && (
        <Spin spinning={loadingOrderDetail}>
          <Row className="row">
            <Col span={24}>
              <h3>{onlineSaleDetailConst.TREATMENTS_TITLE}</h3>
              <Table
                rowKey="purchasedProductId"
                pagination={false}
                scroll={{ x: 1200 }}
                dataSource={onlineDetail?.treatments || []}
                columns={treatmentColumns}
                locale={{
                  emptyText: onlineSaleDetailConst.EMPTY_STATES.TREATMENTS,
                }}
              />
            </Col>
          </Row>

          <Row className="row">
            <Col span={24}>
              <h3>{onlineSaleDetailConst.SESSIONS_TITLE}</h3>
              <Table
                rowKey="sessionId"
                pagination={false}
                dataSource={onlineDetail?.sessions || []}
                columns={sessionColumns}
                locale={{
                  emptyText: onlineSaleDetailConst.EMPTY_STATES.SESSIONS,
                }}
              />
            </Col>
          </Row>

          <Row className="row">
            <Col span={24}>
              <h3>{onlineSaleDetailConst.TRANSFERS_TITLE}</h3>
              <Table
                rowKey="transferId"
                pagination={false}
                scroll={{ x: 900 }}
                dataSource={onlineDetail?.transfers || []}
                columns={transferColumns}
                locale={{
                  emptyText: onlineSaleDetailConst.EMPTY_STATES.TRANSFERS,
                }}
              />
            </Col>
          </Row>
        </Spin>
      )}
    </>
  );
};
